import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ApplySupplierDocumentLineTranslationInput,
  ApplySupplierDocumentLineTranslationResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { useGT } from 'gt-react';

function translationSavedMessage(
  gt: ReturnType<typeof useGT>,
  result: ApplySupplierDocumentLineTranslationResponse,
): string {
  if (result.removedPrevious) {
    return gt(
      'Future documents will use this match, and the previous one was removed.',
      {
        context:
          'toast when a supplier translation replaced the line previous match',
      },
    );
  }
  if (result.didCreate) {
    return gt('Future documents from this supplier will use this match.', {
      context: 'toast when a new supplier translation was created',
    });
  }
  return gt(
    'This supplier already had a translation. It now includes this term.',
    { context: 'toast when a supplier translation was updated' },
  );
}

/** Save the reviewed line as this supplier's translation for later documents. */
export function useApplySupplierDocumentLineTranslation() {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    ApplySupplierDocumentLineTranslationResponse,
    Error,
    ApplySupplierDocumentLineTranslationInput
  >({
    mutationFn: async (input) => {
      return await rpcClient.accountsPayable.supplierDocumentLines.applyTranslation(
        input,
      );
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsPayable.supplierDocumentLines.searchTranslations.key(),
      });
      showToast({
        title: gt('Supplier translation saved', {
          context: 'toast title after teaching a supplier a product term',
        }),
        message: translationSavedMessage(gt, result),
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        title: gt('Could not update the translation', {
          context: 'toast title when saving a supplier translation fails',
        }),
        message:
          error.message ||
          gt('The match was not saved for this supplier.', {
            context: 'toast when saving a supplier translation fails',
          }),
        color: 'error',
        icon: 'x',
      });
    },
  });
}
